import { HTMLAttributes, ReactElement, useId } from 'react';
import classes from './SelectField.module.css';
import { Label } from '../Label/Label';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { Description } from '../Description/Description';
import cn from 'classnames';

export type SelectOption = {
  label: string;
  value: string;
};

type SelectFieldProps = {
  label: string;
  description?: string;
  name: string;
  value?: string;
  required?: boolean;
  errorMessage?: string;
  w100?: boolean;
  spacing?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
} & Omit<HTMLAttributes<HTMLSelectElement>, 'onChange'>;

export const SelectField = ({
  label,
  description,
  errorMessage,
  required,
  className: givenClassName,
  onChange,
  w100 = false,
  spacing = true,
  options,
  placeholder,
  ...rest
}: SelectFieldProps): ReactElement => {
  const id = rest.name || useId();
  const descriptionId = `${id}-description`;

  const combinedClassName = cn(classes.select, givenClassName, {
    [classes.selectError]: errorMessage,
    [classes.spacing]: spacing,
  });

  return (
    <div style={w100 ? { width: '100%' } : {}}>
      <Label
        label={label}
        htmlFor={id}
        size={'ml'}
        required={required}
        spacing={spacing}
      />
      {description && (
        <Description description={description} id={descriptionId} size={'ml'} />
      )}
      <select
        {...rest}
        className={combinedClassName}
        id={id}
        aria-describedby={description ? descriptionId : undefined}
        onChange={onChange}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {errorMessage && <ErrorMessage error={errorMessage} size={'ml'} />}
    </div>
  );
};
